/**
 * channelSpecs.ts — 2004va
 *
 * Defines what each publishing channel needs.
 * Used to check if a media item meets spec before publish,
 * and to guide the creator on what to prepare.
 *
 * Spotify Canvas has no public API — we package to spec and deliver.
 * Instagram/YouTube/TikTok: API connections in phase 2 (pending Meta approval).
 * Download pack: always available, zero API dependency.
 */

export type ChannelType =
  | 'instagram'
  | 'facebook'
  | 'youtube'
  | 'tiktok'
  | 'spotify_canvas'
  | 'twitter_x'
  | 'linkedin'
  | 'website'
  | 'webhook'
  | 'download_pack'

export type MediaType = 'photo' | 'video' | 'audio' | 'document'

export interface ChannelSpec {
  type:          ChannelType
  label:         string
  description:   string
  accepts:       MediaType[]
  api_available: boolean          // can we publish programmatically right now?
  api_phase:     'live' | '2' | 'none'  // when API support arrives
  formats: {
    photo?: {
      aspect_ratios?: string[]    // e.g. ['1:1', '4:5', '1.91:1']
      min_width?:     number      // px
      max_size_mb?:   number
      formats?:       string[]    // ['jpg','png']
    }
    video?: {
      aspect_ratios?: string[]
      max_duration_s?: number
      min_duration_s?: number
      max_size_mb?:    number
      formats?:        string[]
      loops?:          boolean    // Spotify canvas loops
      resolution?:     string
    }
    audio?: {
      formats?:        string[]
      max_duration_s?: number
    }
    document?: {
      formats?:        string[]
    }
  }
  caption_max_chars?: number
  title_max_chars?:   number
  tags_max?:          number
  notes?:             string      // platform-specific guidance
}

export const CHANNEL_SPECS: Record<ChannelType, ChannelSpec> = {
  instagram: {
    type:          'instagram',
    label:         'Instagram',
    description:   'Feed post, Reel, or Story',
    accepts:       ['photo', 'video'],
    api_available: false,
    api_phase:     '2',
    formats: {
      photo: { aspect_ratios: ['1:1', '4:5', '1.91:1'], min_width: 320, max_size_mb: 8, formats: ['jpg','png'] },
      video: { aspect_ratios: ['9:16', '1:1', '4:5'], max_duration_s: 90, max_size_mb: 4096, formats: ['mp4','mov'] },
    },
    caption_max_chars: 2200,
    tags_max: 30,
    notes: 'Reels perform best at 9:16, under 30s. Feed posts at 1:1 or 4:5.',
  },

  facebook: {
    type:          'facebook',
    label:         'Facebook',
    description:   'Page post or Story',
    accepts:       ['photo', 'video', 'document'],
    api_available: false,
    api_phase:     '2',
    formats: {
      photo: { aspect_ratios: ['1.91:1', '1:1'], min_width: 400, max_size_mb: 4, formats: ['jpg','png'] },
      video: { max_duration_s: 14400, max_size_mb: 10240, formats: ['mp4','mov'] },
    },
    caption_max_chars: 63206,
    notes: 'Pending Meta business verification.',
  },

  youtube: {
    type:          'youtube',
    label:         'YouTube',
    description:   'Upload video or Short',
    accepts:       ['video'],
    api_available: false,
    api_phase:     '2',
    formats: {
      video: {
        aspect_ratios: ['16:9', '9:16'],
        max_size_mb: 256000,
        formats: ['mp4','mov','avi','wmv'],
        resolution: '1080p minimum recommended',
      },
    },
    title_max_chars: 100,
    caption_max_chars: 5000,
    tags_max: 500,
    notes: 'Shorts: under 60s at 9:16. Regular: 16:9, any length.',
  },

  tiktok: {
    type:          'tiktok',
    label:         'TikTok',
    description:   'Short video post',
    accepts:       ['video'],
    api_available: false,
    api_phase:     '2',
    formats: {
      video: {
        aspect_ratios: ['9:16'],
        min_duration_s: 3,
        max_duration_s: 600,
        max_size_mb: 4096,
        formats: ['mp4','mov'],
      },
    },
    caption_max_chars: 2200,
    tags_max: 100,
    notes: '9:16 vertical required. 15–60s performs best.',
  },

  spotify_canvas: {
    type:          'spotify_canvas',
    label:         'Spotify Canvas',
    description:   'Looping background for your track',
    accepts:       ['video'],
    api_available: false,
    api_phase:     'none',  // no public API — manual upload only
    formats: {
      video: {
        aspect_ratios: ['9:16'],
        min_duration_s: 3,
        max_duration_s: 8,
        max_size_mb: 50,
        formats: ['mp4'],
        loops: true,
        resolution: '720x1280 minimum',
      },
    },
    notes: 'No public API — The Room packages your clip to spec. You upload manually in Spotify for Artists. Clip must be 3–8s, 9:16, looping MP4.',
  },

  twitter_x: {
    type:          'twitter_x',
    label:         'X / Twitter',
    description:   'Post with media',
    accepts:       ['photo', 'video'],
    api_available: false,
    api_phase:     '2',
    formats: {
      photo: { max_size_mb: 5, formats: ['jpg','png','gif'] },
      video: { max_duration_s: 140, max_size_mb: 512, formats: ['mp4','mov'] },
    },
    caption_max_chars: 280,
    notes: 'Up to 4 photos per post. Video under 2m20s.',
  },

  linkedin: {
    type:          'linkedin',
    label:         'LinkedIn',
    description:   'Professional post',
    accepts:       ['photo', 'video', 'document'],
    api_available: false,
    api_phase:     '2',
    formats: {
      photo: { max_size_mb: 8, formats: ['jpg','png'] },
      video: { max_duration_s: 600, max_size_mb: 5120, formats: ['mp4'] },
      document: { formats: ['pdf'] },
    },
    caption_max_chars: 3000,
    notes: 'Documents (PDFs) render as carousel posts.',
  },

  website: {
    type:          'website',
    label:         'Website / Press page',
    description:   'Publish to your own site or press pack',
    accepts:       ['photo', 'video', 'audio', 'document'],
    api_available: false,
    api_phase:     '2',
    formats: {},
    notes: 'Provide your press page URL or CMS webhook. The Room delivers the file and metadata.',
  },

  webhook: {
    type:          'webhook',
    label:         'Webhook',
    description:   'POST media + metadata to your endpoint',
    accepts:       ['photo', 'video', 'audio', 'document'],
    api_available: true,
    api_phase:     'live',
    formats: {},
    notes: 'The Room POSTs a JSON payload with the media URL, AI metadata, grade, and rights info to your endpoint. No credentials required — just a URL.',
  },

  download_pack: {
    type:          'download_pack',
    label:         'Download pack',
    description:   'Get the file + metadata ready to post anywhere',
    accepts:       ['photo', 'video', 'audio', 'document'],
    api_available: true,
    api_phase:     'live',
    formats: {},
    notes: 'Always available. Includes original file, AI-suggested caption, tags, and spec-formatted versions where applicable.',
  },
}

// ── Spec checker ──────────────────────────────────────────────────────────────
// Given a media item's properties, returns pass/fail/warn per relevant spec

export interface SpecResult {
  channel:  ChannelType
  eligible: boolean           // can this media type be published here at all?
  passes:   string[]          // specs that are met
  warnings: string[]          // specs we can't verify client-side
  fails:    string[]          // specs that are definitely not met
  ready:    boolean           // eligible + no fails
}

export function checkSpec(params: {
  channelType:  ChannelType
  mediaType:    MediaType
  duration_s?:  number        // video/audio duration if known
  width?:       number        // photo/video width if known
  height?:      number        // photo/video height if known
  file_size_mb?: number
  file_format?: string        // e.g. 'mp4', 'jpg'
}): SpecResult {
  const { channelType, mediaType, duration_s, width, height, file_size_mb, file_format } = params
  const spec    = CHANNEL_SPECS[channelType]
  const passes:   string[] = []
  const warnings: string[] = []
  const fails:    string[] = []

  // Eligibility — does this channel accept this media type?
  const eligible = spec.accepts.includes(mediaType)
  if (!eligible) {
    return { channel: channelType, eligible: false, passes, warnings, fails, ready: false }
  }

  const typeSpec = spec.formats[mediaType]
  if (!typeSpec) {
    warnings.push('No format requirements — should work')
    return { channel: channelType, eligible: true, passes, warnings, fails, ready: true }
  }

  // Video checks
  if (mediaType === 'video' && 'max_duration_s' in typeSpec) {
    const vSpec = typeSpec as NonNullable<ChannelSpec['formats']['video']>

    if (duration_s !== undefined) {
      if (vSpec.max_duration_s && duration_s > vSpec.max_duration_s)
        fails.push(`Too long: ${duration_s}s (max ${vSpec.max_duration_s}s)`)
      else if (vSpec.max_duration_s)
        passes.push(`Duration OK: ${duration_s}s`)

      if (vSpec.min_duration_s && duration_s < vSpec.min_duration_s)
        fails.push(`Too short: ${duration_s}s (min ${vSpec.min_duration_s}s)`)
    } else {
      warnings.push('Duration unknown — check manually')
    }

    if (width && height) {
      const ratio = width / height
      if (vSpec.aspect_ratios) {
        const ratioMatch = vSpec.aspect_ratios.some(r => {
          const [w, h] = r.split(':').map(Number)
          return Math.abs(ratio - w / h) < 0.05
        })
        if (ratioMatch) passes.push(`Aspect ratio OK`)
        else            fails.push(`Wrong ratio: ${width}×${height} — need ${vSpec.aspect_ratios.join(' or ')}`)
      }
    } else if (vSpec.aspect_ratios) {
      warnings.push(`Check aspect ratio: need ${vSpec.aspect_ratios.join(' or ')}`)
    }

    if (file_format && vSpec.formats) {
      if (vSpec.formats.includes(file_format.toLowerCase()))
        passes.push(`Format OK: ${file_format}`)
      else
        fails.push(`Wrong format: ${file_format} (need ${vSpec.formats.join('/')})`)
    }
  }

  // Photo checks
  if (mediaType === 'photo' && 'min_width' in typeSpec) {
    const pSpec = typeSpec as NonNullable<ChannelSpec['formats']['photo']>

    if (width && pSpec.min_width) {
      if (width < pSpec.min_width) fails.push(`Too small: ${width}px (min ${pSpec.min_width}px)`)
      else passes.push(`Resolution OK: ${width}px`)
    } else if (pSpec.min_width) {
      warnings.push(`Check resolution: min ${pSpec.min_width}px wide`)
    }

    if (file_size_mb && pSpec.max_size_mb) {
      if (file_size_mb > pSpec.max_size_mb) fails.push(`Too large: ${file_size_mb}MB (max ${pSpec.max_size_mb}MB)`)
      else passes.push(`File size OK`)
    }
  }

  const ready = eligible && fails.length === 0
  return { channel: channelType, eligible, passes, warnings, fails, ready }
}

// ── Caption generator ─────────────────────────────────────────────────────────
// Generates a suggested caption from AI metadata for a given channel

export function suggestCaption(params: {
  channel:     ChannelType
  description: string
  mood?:       string
  tags?:       string[]
  event_name?: string
  creator_handle?: string
}): string {
  const { channel, description, mood, tags = [], event_name, creator_handle } = params

  const hashTags = tags.slice(0, channel === 'instagram' ? 10 : 5)
    .map(t => `#${t.replace(/\s+/g, '').toLowerCase()}`)
    .join(' ')

  const eventTag  = event_name ? `📍 ${event_name}` : ''
  const moodLine  = mood ? `${mood.charAt(0).toUpperCase() + mood.slice(1)}.` : ''
  const credit    = creator_handle ? `📷 @${creator_handle.replace('@', '')}` : ''

  if (channel === 'instagram' || channel === 'tiktok') {
    return [description, moodLine, eventTag, credit, '', hashTags]
      .filter(Boolean).join('\n')
  }

  if (channel === 'twitter_x') {
    const base = `${description}${event_name ? ` — ${event_name}` : ''}`
    const shortened = base.length > 200 ? base.slice(0, 197) + '…' : base
    return `${shortened} ${hashTags.split(' ').slice(0, 3).join(' ')}`
  }

  if (channel === 'linkedin') {
    return `${description}\n\n${event_name ? `Shot at ${event_name}. ` : ''}${moodLine}\n\n${hashTags}`
  }

  if (channel === 'youtube') {
    return `${description}\n\n${event_name ? `Filmed at ${event_name}.` : ''}\n${hashTags}`
  }

  return `${description}${event_name ? ` — ${event_name}` : ''}`
}
